import {
  ProjectMetadata,
  FootprintMetadata,
  IStorey,
  IStoreyValuesWithElevation,
  IStoreyValues,
  IIndexedStorey,
} from '../models/project.model';
import { isDefined } from './array.helpers';
import {
  expressionVariablesConstants,
  LA_PER_APARTMENT,
} from '../constants/expression-variables.constants';
import { highest, sum } from './math.helpers';
import { getKeys, omit } from './object.helpers';
import { getActivityExpressionVariables } from './activities.helpers';
import {
  getBuildingLengthEstimate,
  getStoreyVariables,
} from './results.helpers';
import { CALCULATIONS } from '../constants/calculations.constants';
import { getBuildingGFA } from './expression-variables.helpers';
import { getBuildingWidth } from './geometry/coordinates.helpers';
import { ActivityId } from '@shared/models/activity-class.model';

export type LegacyStoreyMetadata = ProjectMetadata & {
  below_ground?: number;
  storeys?: IStorey[];
  building_footprint?: FootprintMetadata;
  gfa_building?: number;
  building_perimeter?: number;
  activity_id?: ActivityId;
  activities?: Parameters<typeof getActivityExpressionVariables>[1];
};

type ICalculatedStoreyValues = {
  gfa_above_ground: number;
  gfa_below_ground: number;
  total_height: number;
  total_height_above_ground: number;
  total_height_below_ground: number;
  external_gwa: number;
  external_gwa_above_ground: number;
  external_gwa_below_ground: number;
  gross_volume: number;
  gross_volume_above_ground: number;
  gross_volume_below_ground: number;
  elevation: number;
  gfa_cantilevered_floor_slabs: number;
  gfa_terrace_slabs: number;
  gfa_sky: number;
  gfa_activities: number;
  gfa_facades: number;
  gfa_installations: number;
  gfa_apartments: number;
  la_apartments: number;
  gfa_stairwells: number;
  apartment_count: number;
  stairwell_count: number;
  gwa_apartment_parting_internal_walls: number;
  gwa_stairwell_parting_walls: number;
  gwa_elevator_shaft_walls: number;
  gwa_partition_walls: number;
  gfa_balconies: number;
  gfa_stairs: number;
  gfa_floor_slabs: number;
  gfa_ground_floor_slabs: number;
  length_facade_pillars: number;
  /** storey perimeter of the storey, not floor perimeter! */
  perimeter: number;
  gwa_mezzanine: number;
  occupant_count: number;
};

type ICalculationVariables = {
  is_ground_floor: number;
  /** storey perimeter of the storey, not floor perimeter! */
  perimeter: number;
  mezzanine_edge: number;
  facade_edge: number;
  inner_height: number;
  gfa: number;
  stairwells_apartments_per_stairwell_per_storey: number;
  apartments_living_area_per_apartment: number;
  apartments_balcony_area_per_apartment: number;
  garage_gfa: number;
  gfa_other_activities: number;
  gfa_apartment_plus_stairwell: number;
  gfa_stairwell_per_gfa_apartment: number;
  gfa_openings_per_stairwell_per_storey: number;
  gfa_elevator_per_stairwell_per_storey: number;
  gfa_floor_per_stairwell_per_storey: number;
  gfa_per_stairwell_per_storey: number;
  gfa_stairwell_per_living_area: number;
  gfa_stairs_per_gfa_stairwell: number;
  gfa_floor_per_gfa_stairwell: number;
  facade_pillar_count: number;
};

/** @deprecated */
export type IStoreySum = IStoreyValues & ICalculatedStoreyValues;

/** @deprecated */
export type IEnrichedStorey = ICalculatedStoreyValues & ICalculationVariables;

/** Chart storey sum plus garage GFA (lives on {@link IEnrichedStorey}, not {@link IStoreySum}). */
export type VersionStoreySum = IStoreySum & {
  garage_gfa: number;
};

/**
 * Sum per-storey {@link IEnrichedStorey.garage_gfa} for charts that cannot read `getActivity`.
 */
export const sumGarageGfaFromEnrichedStoreys = (
  storeys: Pick<IEnrichedStorey, 'garage_gfa'>[],
): number => storeys.reduce((acc, storey) => acc + storey.garage_gfa, 0);

/** @deprecated */
export type StoreySelector = number | string | undefined;

/** @deprecated */
const emptyStorey: Readonly<IStoreyValues> = {
  gfa: 0,
  facade_edge: 0,
  mezzanine_edge: 0,
  inner_height: 0,
};

/** @deprecated */
export const emptyStoreySummedValues: Readonly<IStoreySum> = {
  ...emptyStorey,
  inner_height: 0,
  gfa_above_ground: 0,
  gfa_below_ground: 0,
  total_height: 0,
  total_height_above_ground: 0,
  total_height_below_ground: 0,
  external_gwa: 0,
  external_gwa_above_ground: 0,
  external_gwa_below_ground: 0,
  gross_volume: 0,
  gross_volume_above_ground: 0,
  gross_volume_below_ground: 0,
  elevation: 0,
  gfa_cantilevered_floor_slabs: 0,
  gfa_terrace_slabs: 0,
  gfa_sky: 0,
  gfa_activities: 0,
  gfa_facades: 0,
  gfa_installations: 0,
  la_apartments: 0,
  gfa_apartments: 0,
  gfa_stairwells: 0,
  apartment_count: 0,
  stairwell_count: 0,
  gwa_apartment_parting_internal_walls: 0,
  gwa_stairwell_parting_walls: 0,
  gwa_elevator_shaft_walls: 0,
  gwa_partition_walls: 0,
  gfa_balconies: 0,
  gfa_stairs: 0,
  gfa_floor_slabs: 0,
  gfa_ground_floor_slabs: 0,
  length_facade_pillars: 0,
  perimeter: 0,
  gwa_mezzanine: 0,
  occupant_count: 0,
};

/**
 * @deprecated Get a list of storeys that match the selectors.
 * If no selectors are provided, all storeys are returned.
 * @param storeys List of all storeys
 * @param selectors Either indexes (from 0) or names of storeys
 * @returns List of storeys with their original index
 */
const getStoreys = <StoreyType extends IStorey>(
  storeys: StoreyType[] = [],
  ...selectors: StoreySelector[]
): IIndexedStorey<StoreyType>[] => {
  if (selectors.length === 0) {
    return [...storeys].map((storey, index) => ({ floorIndex: index, storey }));
  }

  const names = new Set(
    selectors
      .filter((n): n is string => typeof n === 'string' && n !== '')
      .map((n) => n.toLowerCase()),
  );
  const indexes = new Set(selectors.filter((n) => typeof n === 'number'));

  return storeys
    .map((storey, index) => ({ floorIndex: index, storey }))
    .filter(({ floorIndex, storey }) => {
      const name = storey.name?.toLowerCase();
      return (name && names.has(name)) || indexes.has(floorIndex);
    });
};

/**
 * Get gfa & building perimeter from ProjectMetaData
 *
 * @param meta ProjectMetaData
 * @returns { gfa: number, perimeter: number }
 */
const getGeoSettings = (
  meta: LegacyStoreyMetadata,
): { gfa: number; perimeter: number } => ({
  gfa: getBuildingGFA(meta),
  perimeter: meta.building_perimeter ?? meta.building_footprint?.perimeter ?? 0,
});

type GFASumAndPerimeterMax = { gfaSum: number; perimeterMax: number };

/**
 * Get the sum of gfa and the max building perimeter
 * @param storeys
 * @param buildingGeoPerimeter
 * @returns
 */
const getGFASumAndPerimeterMax = (
  storeys: IStorey[],
  buildingGeoPerimeter: number,
): GFASumAndPerimeterMax =>
  storeys.reduce<GFASumAndPerimeterMax>(
    (acc, { gfa = 0, facade_edge = 0, mezzanine_edge = 0 }) => {
      // If mezzanine edge is defined, building perimeter will be inherited from a storey below with no mezzanine edge
      const perimeter =
        mezzanine_edge > 0 ? acc.perimeterMax : facade_edge + mezzanine_edge;
      return {
        gfaSum: acc.gfaSum + gfa,
        perimeterMax: Math.max(acc.perimeterMax, perimeter),
      };
    },
    { gfaSum: 0, perimeterMax: buildingGeoPerimeter },
  );

const getNumberOfUndefinedGFAAndPerimeterStoreys = (
  storeys: IStorey[],
): number =>
  storeys.filter(
    ({ gfa, facade_edge }) => !isDefined(gfa) && !isDefined(facade_edge),
  ).length;

/**
 * @deprecatedGet storeys with fallbacks (also known as auto-values)
 * @param meta
 * @returns
 */
export const getStoreysWithFallbacks = (
  meta: LegacyStoreyMetadata,
): IStoreyValues[] => {
  const { storeys = [] } = meta;

  // First go through all storeys that have perimeter or gfa (to know how much is left)
  const gfas: (number | undefined)[] = storeys.map((storey) => {
    if (isDefined(storey.gfa) || isDefined(storey.facade_edge)) {
      return getStoreyGFAWithFallback(storey, meta);
    }
  });
  // Total GFA "taken up" by storeys
  const totalGFA = sum(gfas);

  const storeyCalculations = storeys.map((storey, index) => {
    const { name, inner_height, mezzanine_edge } = storey;
    const gfa = gfas[index] ?? getStoreyGFAWithFallback(storey, meta, totalGFA);
    const facade_edge = getFacadeEdgeWithFallback(meta, storey, gfa);

    return {
      name,
      gfa,
      facade_edge,
      mezzanine_edge: mezzanine_edge ?? 0,
      inner_height: typeof inner_height === 'number' ? inner_height : 2.7,
    };
  });

  return limitStoreyFallbackGFA(meta, storeyCalculations);
};

/**
 * Prevent users from actively overflowing the building GFA (by entering too large perimeter values)
 * @param meta
 * @param storeyCalculations
 * @returns
 */
const limitStoreyFallbackGFA = (
  meta: LegacyStoreyMetadata,
  storeyCalculations: IStoreyValues[],
): IStoreyValues[] => {
  const { storeys = [], gfa_building } = meta;
  // If gfa_building is undefined, gfa is unlimited
  if (gfa_building === undefined) return storeyCalculations;

  const newTotalGFA = sum(storeyCalculations, 'gfa');
  if (newTotalGFA <= gfa_building) {
    // total gfa is within limits, no need to adjust
    return storeyCalculations;
  }

  const gfaSums = storeys.reduce(
    (acc, storey, index) => {
      const calc = storeyCalculations[index];
      if (!calc) return acc;
      if (storey.gfa !== undefined) acc.manual += calc.gfa;
      else acc.estimated = acc.estimated += calc.gfa;
      return acc;
    },
    { manual: 0, estimated: 0 },
  );

  if (gfaSums.manual > gfa_building) {
    // manual values already overflow. cut all estimated values
    return storeyCalculations.map((calc, i) => {
      const storey = storeys[i] ?? {};
      if (storey.gfa !== undefined) return calc;
      return { ...calc, gfa: 0 };
    });
  }

  const remainingGFA = gfa_building - gfaSums.manual;
  if (gfaSums.estimated > remainingGFA) {
    // estimated values already overflow remaining gfa. Scale down all estimated values proportionally to fit remaining gfa
    const scaleFactor = remainingGFA / gfaSums.estimated;
    return storeyCalculations.map((calc, i) => {
      const storey = storeys[i] ?? {};
      if (storey.gfa !== undefined) return calc;
      return {
        ...calc,
        gfa: Math.max(0, Math.round(calc.gfa * scaleFactor)),
      };
    });
  }

  return storeyCalculations;
};

/**
 * returns the perimeter of the floor.
 * @param storey
 * @returns floor perimeter or undefined
 */
const getFloorPerimeter = (storey: IStorey) => {
  const { facade_edge, mezzanine_edge = 0 } = storey;
  // if perimeter is undefined, we cannot know the floor perimeter.
  if (facade_edge === undefined) return undefined;
  return facade_edge + mezzanine_edge;
};

// this method returns estimates
const getStoreyGFAWithFallback = (
  storey: IStorey,
  meta: LegacyStoreyMetadata,
  totalGFA = 0,
): number => {
  const { gfa } = storey;
  if (gfa !== undefined) return gfa;

  const { storeys = [], building_footprint, gfa_building } = meta;

  // User has defined facade_edge and optionally mezzanine_edge, calculate gfa based on floor perimeter
  const floorPerimeter = getFloorPerimeter(storey);
  if (floorPerimeter !== undefined) {
    const footprintArea = building_footprint?.area ?? 0;
    const width = getBuildingWidth(building_footprint?.coordinates);
    // Assume that all increase/decrease affects the length and not the width
    const lengthIncrease =
      (floorPerimeter - (building_footprint?.perimeter ?? 0)) / 2;
    const estimatedGFA = Math.max(0, footprintArea + lengthIncrease * width);
    return Math.min(estimatedGFA, gfa_building ?? Infinity);
  }

  const numberOfUndefinedGFAStoreys =
    getNumberOfUndefinedGFAAndPerimeterStoreys(storeys);
  // Should not happen since this storey.gfa must be undefined
  if (numberOfUndefinedGFAStoreys === 0) {
    return 0;
  }

  const geoSettings = getGeoSettings(meta);

  return Math.max(
    0,
    (geoSettings.gfa - totalGFA) / numberOfUndefinedGFAStoreys,
  );
};

/** Estimate facade edge */
const getFacadeEdgeWithFallback = (
  meta: LegacyStoreyMetadata,
  storey: IStorey,
  storeyGFA: number,
): number => {
  const { mezzanine_edge = 0, facade_edge } = storey;
  // If facade edge is defined, use it
  if (facade_edge !== undefined) {
    return facade_edge;
  }
  const { storeys = [], building_footprint } = meta;
  const geoSettings = getGeoSettings(meta);
  const { perimeterMax } = getGFASumAndPerimeterMax(
    storeys,
    geoSettings.perimeter,
  );
  const footprintArea = building_footprint?.area ?? 0;
  const width = getBuildingWidth(building_footprint?.coordinates);
  const length = getBuildingLengthEstimate(footprintArea, width);
  const lengthIncrease = length * (storeyGFA / footprintArea - 1);

  // Get new perimeter based on storeyGFA by shortening/increasing the longest side
  const facadeEdgeEstimate = perimeterMax + lengthIncrease * 2 - mezzanine_edge;
  return Math.max(0, facadeEdgeEstimate);
};

/**
 * Legacy expression scope treats missing or empty `storeys` as one placeholder
 * storey so footprint / {@link LegacyStoreyMetadata.gfa_building} fallbacks still
 * produce a storey sum.
 */
export const withPlaceholderStoreyIfEmpty = <T extends LegacyStoreyMetadata>(
  meta: T,
): T & { storeys: IStorey[] } => {
  const { storeys } = meta;
  if (storeys !== undefined && storeys.length > 0) {
    return { ...meta, storeys };
  }
  return { ...meta, storeys: [{}] };
};

/**
 * @deprecated Get a calculated merge of storeys from project metadata.
 * @param selectors Internal 0-based indexes or storey names. Expression `getStorey(n)` is 1-based and is converted before this helper.
 */
export const getStoreySumExpressionVariables = (
  meta: LegacyStoreyMetadata,
  selectors: StoreySelector[] = [],
): { storeys: IEnrichedStorey[]; sum: IStoreySum } => {
  const storeys = getStoreysWithFallbacks(meta);
  const storeysWithElevation = applyStoreyElevations(
    meta.below_ground ?? 0,
    storeys,
  );

  const summedStoreys = sumStoreys(
    getStoreys(storeysWithElevation, ...selectors),
    meta,
  );
  return summedStoreys;
};

/**
 * @deprecated Since floors with mezzanine edge doesn't have storey perimeter or storey area, we
 * use the values from the previous storey with a mezzanine edge of 0.
 */
export const getStoreyBuildingValues = (
  /** This has to be the full array of storeys */
  storeys: IStoreyValues[],
  /** This is the floor index for which we want the values */
  floorIndex: number,
): { storeyArea: number; storeyPerimeter: number } => {
  const [firstStorey] = storeys;
  // there has to be storeys, otherwise return zeros
  if (!firstStorey) {
    return {
      storeyArea: 0,
      storeyPerimeter: 0,
    };
  }
  // if index is out of bounds, there is no storey and therefore no storey area or perimeter
  if (floorIndex < 0 || floorIndex >= storeys.length) {
    return {
      storeyArea: 0,
      storeyPerimeter: 0,
    };
  }
  // traverse storeys backwards to find the first storey that has no mezzanine edge
  for (let i = floorIndex; i >= 0; i--) {
    const storey = storeys[i];
    if (!storey) continue; // this really should never happen
    if (storey.mezzanine_edge > 0) continue; // mezzanine floors cannot give storey area or perimeter
    return {
      storeyArea: storey.gfa,
      storeyPerimeter: storey.facade_edge,
    };
  }

  // we should only get here if first storey has mezzanine edge. ignore first storey mezzanine edge in this case
  return {
    storeyArea: firstStorey.gfa,
    storeyPerimeter: firstStorey.facade_edge,
  };
};

/**
 * @deprecated Sum all provided storeys.
 * @param selectedStoreys
 */
const sumStoreys = (
  storeys: IIndexedStorey<IStoreyValuesWithElevation>[],
  meta: LegacyStoreyMetadata,
  defaults: Omit<IStoreyValuesWithElevation, 'isGroundFloor'> = {
    gfa: 0,
    facade_edge: 0,
    mezzanine_edge: 0,
    inner_height: 0,
    elevation: 0,
  },
): { storeys: IEnrichedStorey[]; sum: IStoreySum } => {
  if (storeys.length === 0) {
    return { storeys: [], sum: { ...emptyStoreySummedValues } };
  }

  const results: {
    storeys: IEnrichedStorey[];
    sum: IStoreySum;
  } = {
    storeys: [],
    sum: {
      ...emptyStoreySummedValues,
      elevation: getElevation(meta.below_ground ?? 0),
    },
  };

  // Keys for properties that can be summarized
  const sumKeys = getKeys(omit(results.sum, 'name'));

  const sumGFA = storeys.reduce(
    (acc, { storey }) => acc + (storey.gfa ?? defaults.gfa),
    0,
  );

  storeys.forEach(({ floorIndex, storey }) => {
    const baseStorey: Omit<IStoreyValuesWithElevation, 'isGroundFloor'> =
      Object.assign({}, defaults, storey);

    const { gfa, facade_edge, inner_height, elevation } = baseStorey;
    const atemp = gfa * 0.9;

    // make absolute sure that first storey cannot have mezzanine edge for calculations
    const mezzanine_edge = floorIndex === 0 ? 0 : storey.mezzanine_edge;

    const { storeyPerimeter } = getStoreyBuildingValues(
      getStoreysWithFallbacks(meta),
      floorIndex,
    );
    const {
      total_height,
      total_height_above_ground,
      total_height_below_ground,
      gfa_above_ground,
      gfa_below_ground,
      external_gwa,
      external_gwa_above_ground,
      external_gwa_below_ground,
      gross_volume,
      gross_volume_above_ground,
      gross_volume_below_ground,
      gfa_ground_floor_slabs,
      gfa_cantilevered_floor_slabs,
      gfa_terrace_slabs,
      gfa_sky,
      gfa_activities,
    } = getStoreyVariables(storey, floorIndex, meta);

    const {
      stairwells_apartments_per_stairwell_per_storey = 0, // returns same for every storey
      stairwells_elevators_per_stairwell = 0, // returns same for every storey
      apartments_living_area_per_apartment = LA_PER_APARTMENT, // returns same for every storey
      garage_gfa = 0, // returns value divided per storey
      gfa_other_activities = 0, // returns value divided per storey
    } = getActivityExpressionVariables(
      {
        gfa,
        sumGFA,
      },
      meta.activities,
    );

    const gfa_openings_per_stairwell_per_storey =
      CALCULATIONS.gfa_openings_per_stairwell_per_storey.calculate();

    const gfa_elevator_per_stairwell_per_storey =
      CALCULATIONS.gfa_elevator_per_stairwell_per_storey.calculate({
        elevators_per_stairwell: stairwells_elevators_per_stairwell,
      });

    const gfa_floor_per_stairwell_per_storey =
      CALCULATIONS.gfa_floor_per_stairwell_per_storey.calculate({
        stairwells_apartments_per_stairwell_per_storey,
      });

    const gfa_per_stairwell_per_storey =
      CALCULATIONS.gfa_per_stairwell_per_storey.calculate({
        gfa_openings_per_stairwell_per_storey,
        gfa_elevator_per_stairwell_per_storey,
        gfa_floor_per_stairwell_per_storey,
      });

    const gfa_stairwell_per_living_area =
      CALCULATIONS.gfa_stairwell_per_living_area.calculate({
        stairwells_apartments_per_stairwell_per_storey,
        apartments_living_area_per_apartment,
        gfa_per_stairwell_per_storey,
      });

    const gfa_stairs_per_gfa_stairwell =
      CALCULATIONS.gfa_stairs_per_gfa_stairwell.calculate({
        gfa_per_stairwell_per_storey,
      });

    const gfa_floor_per_gfa_stairwell =
      CALCULATIONS.gfa_floor_per_gfa_stairwell.calculate({
        gfa_floor_per_stairwell_per_storey,
        gfa_per_stairwell_per_storey,
      });

    const gfa_facades = CALCULATIONS.gfa_facades.calculate({
      perimeter: storeyPerimeter,
    });
    const gfa_installations = CALCULATIONS.gfa_installations.calculate({ gfa });

    const gfa_apartment_plus_stairwell =
      CALCULATIONS.gfa_apartment_plus_stairwell.calculate({
        gfa_activities,
        gfa_facades,
        gfa_installations,
        garage_gfa,
        gfa_other_activities,
      });

    const gfa_stairwell_per_gfa_apartment =
      CALCULATIONS.gfa_stairwell_per_gfa_apartment.calculate({
        gfa_stairwell_per_living_area,
      });

    const gfa_apartments = CALCULATIONS.gfa_apartments.calculate({
      gfa_apartment_plus_stairwell,
      gfa_stairwell_per_gfa_apartment,
    });

    const la_apartments = CALCULATIONS.la_apartments.calculate({
      gfa_apartments,
    });

    const gfa_stairwells = CALCULATIONS.gfa_stairwells.calculate({
      gfa_apartment_plus_stairwell,
      gfa_apartments,
    });
    const apartment_count = CALCULATIONS.apartment_count.calculate({
      apartments_living_area_per_apartment,
      la_apartments,
    });
    const stairwell_count = CALCULATIONS.stairwell_count.calculate({
      apartment_count,
      stairwells_apartments_per_stairwell_per_storey,
    });

    const gwa_apartment_parting_internal_walls =
      CALCULATIONS.gwa_apartment_parting_internal_walls.calculate({
        apartments_living_area_per_apartment,
        apartment_count,
        inner_height,
      });
    const gwa_stairwell_parting_walls =
      CALCULATIONS.gwa_stairwell_parting_walls.calculate({
        gfa_stairwells,
        inner_height,
      });
    const gwa_elevator_shaft_walls =
      CALCULATIONS.gwa_elevator_shaft_walls.calculate({
        stairwell_count,
        inner_height,
        gfa_elevator_per_stairwell_per_storey,
      });
    const gwa_partition_walls = CALCULATIONS.gwa_partition_walls.calculate({
      la_apartments,
      inner_height,
    });

    const gwa_mezzanine = CALCULATIONS.gwa_mezzanine.calculate({
      mezzanine_edge,
      total_height,
    });

    const occupant_count = CALCULATIONS.occupant_count.calculate({
      activity: meta.activity_id,
      atemp,
      apartments_living_area_per_apartment,
    });

    const {
      apartments_balcony_area_per_apartment, // checks for undefined/null below
    } = getActivityExpressionVariables(
      {
        gfa,
        sumGFA,
      },
      meta.activities,
    );

    const gfa_balconies = CALCULATIONS.gfa_balconies.calculate({
      apartments_balcony_area_per_apartment,
      apartment_count,
    });
    const gfa_stairs = CALCULATIONS.gfa_stairs.calculate({
      gfa_stairs_per_gfa_stairwell,
      gfa_stairwells,
    });
    const gfa_floor_slabs = CALCULATIONS.gfa_floor_slabs.calculate({
      is_ground_floor: Number(storey.isGroundFloor),
      gfa_stairwells,
      gfa_floor_per_gfa_stairwell,
      gfa_installations,
      garage_gfa,
      gfa_other_activities,
      gfa_apartments,
    });
    const facade_pillar_count = CALCULATIONS.facade_pillar_count.calculate({
      perimeter: storeyPerimeter,
      apartment_count,
    });
    const length_facade_pillars = CALCULATIONS.length_facade_pillars.calculate({
      facade_pillar_count,
      inner_height,
    });

    const storeyValuesToAddToSum: IStoreySum = {
      perimeter: storeyPerimeter,
      facade_edge,
      mezzanine_edge,
      inner_height,
      gfa,
      gfa_above_ground,
      gfa_below_ground,
      total_height,
      total_height_above_ground,
      total_height_below_ground,
      external_gwa,
      external_gwa_above_ground,
      external_gwa_below_ground,
      gross_volume,
      gross_volume_above_ground,
      gross_volume_below_ground,
      elevation: Math.min(elevation, results.sum.elevation),
      gfa_cantilevered_floor_slabs,
      gfa_terrace_slabs,
      gfa_sky,
      gfa_activities,
      gfa_facades,
      gfa_installations,
      la_apartments,
      gfa_apartments,
      gfa_stairwells,
      apartment_count,
      stairwell_count,
      gwa_apartment_parting_internal_walls,
      gwa_stairwell_parting_walls,
      gwa_elevator_shaft_walls,
      gwa_partition_walls,
      gfa_balconies,
      gfa_stairs,
      gfa_floor_slabs,
      gfa_ground_floor_slabs,
      length_facade_pillars,
      gwa_mezzanine,
      occupant_count,
    };

    results.storeys.push({
      is_ground_floor: Number(storey.isGroundFloor),
      perimeter: storeyPerimeter,
      mezzanine_edge,
      facade_edge,
      inner_height,
      gfa,
      gfa_above_ground,
      gfa_below_ground,
      total_height,
      total_height_above_ground,
      total_height_below_ground,
      external_gwa,
      external_gwa_above_ground,
      external_gwa_below_ground,
      gross_volume,
      gross_volume_above_ground,
      gross_volume_below_ground,
      elevation,
      gfa_cantilevered_floor_slabs,
      gfa_terrace_slabs,
      gfa_sky,
      gfa_activities,
      stairwells_apartments_per_stairwell_per_storey,
      apartments_living_area_per_apartment,
      apartments_balcony_area_per_apartment,
      garage_gfa,
      gfa_other_activities,
      gfa_facades,
      gfa_installations,
      gfa_apartment_plus_stairwell,
      gfa_stairwell_per_gfa_apartment,
      gfa_apartments,
      la_apartments,
      gfa_stairwells,
      apartment_count,
      stairwell_count,
      gwa_apartment_parting_internal_walls,
      gwa_stairwell_parting_walls,
      gwa_elevator_shaft_walls,
      gwa_partition_walls,
      gfa_balconies,
      gfa_stairs,
      gfa_floor_slabs,
      gfa_ground_floor_slabs,
      length_facade_pillars,
      gfa_openings_per_stairwell_per_storey,
      gfa_elevator_per_stairwell_per_storey,
      gfa_floor_per_stairwell_per_storey,
      gfa_per_stairwell_per_storey,
      gfa_stairwell_per_living_area,
      gfa_stairs_per_gfa_stairwell,
      gfa_floor_per_gfa_stairwell,
      facade_pillar_count,
      gwa_mezzanine,
      occupant_count,
    });

    // Add values to sum
    sumKeys.forEach((key) => {
      const value = storeyValuesToAddToSum[key];
      if (typeof value === 'number') {
        results.sum[key] += value;
      }
    });
  });

  // Static (not summed) values
  results.sum.perimeter = highest(results.storeys, 'perimeter');

  // Add names of all storeys
  results.sum.name = joinNames(storeys);

  return results;
};

/** @deprecated */
export const applyStoreyElevations = (
  below_ground: number,
  storeys: IStoreyValues[] = [],
): IStoreyValuesWithElevation[] => {
  const elevationStoreys: IStoreyValuesWithElevation[] = [];
  const slab = expressionVariablesConstants.floor_slab_height;
  let elevation = getElevation(below_ground);
  storeys.forEach((storey, index) => {
    elevationStoreys.push({ ...storey, elevation, isGroundFloor: index === 0 });
    elevation += (storey.inner_height ?? 0) + slab;
  });
  return elevationStoreys;
};

/**
 * To not return -0 and break tests
 * @param below_ground
 * @returns
 */
const getElevation = (below_ground = 0): number => -below_ground || 0;

/**
 * Join names of storeys.
 * @param storeys
 * @returns
 */
const joinNames = (storeys: IIndexedStorey<IStorey>[]): string =>
  storeys.map(({ storey }, i) => storey.name || `Storey ${i}`).join(', ');
